import {
  ProductCurrencyType,
  ProductTypeType,
  type CreateProductInputType,
  type ProductPriceInputType,
  type ProductPricePeriodInputType,
  type ProductQueryType,
  type UpdateProductInputType,
} from "@/types/graphql/partner.generated";
import type {
  ProductFormPrice,
  ProductFormPricePeriod,
  ProductFormValues,
} from "./types";

let localKeySeq = 0;

/** Стабильный локальный ключ для новых цен/окон — никогда не уходит на бэкенд. */
export function createLocalKey(): string {
  localKeySeq += 1;
  return `local-${Date.now()}-${localKeySeq}`;
}

export const DEFAULT_PRODUCT_FORM_VALUES: ProductFormValues = {
  type: ProductTypeType.PpvType,
  title: "",
  description: null,
  previewId: null,
  eventStartAt: null,
  eventFinishAt: null,
  startAt: null,
  finishAt: null,
  finishSalesAt: null,
  showContent: true,
  hideInGeneralLists: false,
  perContentPurchase: false,
  prices: [],
};

/**
 * Дефолты новой цены (référence: PricesField.onClickAdd) — period=7 для
 * подписки/null для разового билета, одно окно с dateFrom=defaultDateFrom
 * (обычно текущая publishAt формы) || now.
 *
 * currency по умолчанию — "USD", ЕСЛИ платформе доступно более одной валюты
 * (или availableCurrencies не передан) — тогда и так есть из чего выбрать в
 * уже отфильтрованном CurrencyField. Но если платформа подключила ТОЛЬКО
 * Paynet, доступна ровно одна валюта (MDL), и USD по умолчанию был бы
 * гарантированно недоступен: основной сценарий фичи (молдавская платформа
 * без Stripe) на КАЖДОМ создании продукта упирался бы в ошибку валидации на
 * первом же «Сохранить» — ровно тот путь, ради которого фича и делается.
 * Поэтому при единственной доступной валюте подставляем её, а не USD.
 */
export function createEmptyPrice(
  productType: ProductTypeType,
  defaultDateFrom?: string | null,
  availableCurrencies?: ProductCurrencyType[]
): ProductFormPrice {
  const currency =
    availableCurrencies?.length === 1
      ? availableCurrencies[0]
      : ProductCurrencyType.UsdType;

  return {
    key: createLocalKey(),
    currency,
    period: productType === ProductTypeType.SubscriptionType ? 7 : null,
    trialPeriod: null,
    trialValue: "",
    countryAccessId: null,
    periods: [
      {
        key: createLocalKey(),
        dateFrom: defaultDateFrom || new Date().toISOString(),
        value: "",
        oldValue: "",
      },
    ],
  };
}

type ProductQueryData = NonNullable<ProductQueryType["product"]>;

export function mapProductToFormValues(
  product: ProductQueryData
): ProductFormValues {
  return {
    type: product.type,
    title: product.title || "",
    description: product.description ?? null,
    previewId: product.preview?.id ?? null,
    eventStartAt: product.eventStartAt ?? null,
    eventFinishAt: product.eventFinishAt ?? null,
    startAt: product.startAt ?? null,
    finishAt: product.finishAt ?? null,
    finishSalesAt: product.finishSalesAt ?? null,
    showContent: product.showContent ?? true,
    hideInGeneralLists: product.hideInGeneralLists ?? false,
    perContentPurchase: product.perContentPurchase ?? false,
    prices: product.prices.map(
      (price): ProductFormPrice => ({
        key: createLocalKey(),
        id: price.id,
        currency: price.currency,
        period: price.period,
        trialPeriod: price.trialPeriod || null,
        trialValue: price.trialValue ? String(price.trialValue) : "",
        countryAccessId: price.countryAccessId ?? null,
        periods: price.periods.map(
          (period): ProductFormPricePeriod => ({
            key: createLocalKey(),
            id: period.id,
            dateFrom: period.dateFrom,
            value: period.value != null ? String(period.value) : "",
            oldValue: period.oldValue != null ? String(period.oldValue) : "",
          })
        ),
      })
    ),
  };
}

/**
 * Минимальная дата среди prices[].periods[].dateFrom — используется и как
 * дефолтный dateFrom новой цены (référence: startAt проп PricesField), и как
 * итоговый publishAt при публикации (référence: ProductForm.getPublishAt).
 */
export function getMinPricesDateFrom(
  prices: ProductFormPrice[]
): string | null {
  const dates = prices
    .flatMap((price) => price.periods.map((period) => period.dateFrom))
    .filter((date): date is string => Boolean(date));

  if (dates.length === 0) return null;

  return dates.reduce((min, date) =>
    new Date(date) < new Date(min) ? date : min
  );
}

/** Валидно ли одно ценовое окно — dateFrom задан, value непустая и
 * конечное число (см. PriceEditDialog.canSave). */
export function isPricePeriodValid(period: ProductFormPricePeriod): boolean {
  if (!period.dateFrom) return false;
  const trimmed = period.value.trim();
  return trimmed !== "" && Number.isFinite(Number(trimmed));
}

/** Валидна ли цена целиком — период (билета/подписки) задан, КАЖДОЕ окно
 * валидно, и (вторая линия обороны — см. backend
 * ProductValidator.SUBSCRIPTION_MDL_MESSAGE) цена SUBSCRIPTION не в MDL:
 * Paynet не поддерживает сохранение карты для продления подписки (см.
 * PriceEditDialog.canSave — гейт на кнопке «Сохранить»). */
export function isPriceValid(
  price: ProductFormPrice,
  productType?: ProductTypeType
): boolean {
  const isSubscriptionMdl =
    productType === ProductTypeType.SubscriptionType &&
    price.currency === ProductCurrencyType.MdlType;

  return Boolean(
    !isSubscriptionMdl &&
      price.period &&
      price.periods.length > 0 &&
      price.periods.every(isPricePeriodValid)
  );
}

/**
 * Строка -> число для ОБЯЗАТЕЛЬНОГО поля (value окна). PriceEditDialog не
 * даёт сохранить окно с пустым/нечисловым value (см. canSave), поэтому в
 * норме сюда всегда приходит валидная строка — Number.isFinite здесь чисто
 * защита от NaN «по пути» (не должен уйти в GraphQL-мутацию молча как 0).
 */
function toRequiredNumber(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** То же самое для НЕОБЯЗАТЕЛЬНЫХ полей (oldValue, trialValue) — пустая
 * строка или нечисловой ввод -> undefined (поле не отправляется), а не NaN. */
function toOptionalNumber(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function toPricePeriodInput(
  period: ProductFormPricePeriod
): ProductPricePeriodInputType {
  return {
    id: period.id,
    dateFrom: period.dateFrom as string,
    value: toRequiredNumber(period.value),
    oldValue: toOptionalNumber(period.oldValue),
  };
}

function toPriceInput(price: ProductFormPrice): ProductPriceInputType {
  return {
    id: price.id,
    currency: price.currency,
    period: price.period as number,
    trialPeriod: price.trialPeriod ?? undefined,
    trialValue: toOptionalNumber(price.trialValue),
    countryAccessId: price.countryAccessId,
    periods: price.periods.map(toPricePeriodInput),
  };
}

/**
 * Поля, общие для Create/Update-инпутов продукта — всё, КРОМЕ `type`
 * (Product.type immutable, UpdateProductInput его не содержит вовсе, см.
 * buildProductInput/buildUpdateProductInput ниже и C1 в финальном ревью).
 * publishAt считает вызывающая сторона (см. ProductForm —
 * «Опубликовать»/«Черновик»), поэтому передаётся отдельным параметром.
 */
function buildCommonProductInput(
  values: ProductFormValues,
  publishAt: string | null
): Omit<CreateProductInputType, "type"> {
  return {
    title: values.title,
    description: values.description,
    previewId: values.previewId,
    eventStartAt: values.eventStartAt,
    eventFinishAt: values.eventFinishAt,
    startAt: values.startAt as string,
    finishAt: values.finishAt as string,
    finishSalesAt: values.finishSalesAt,
    showContent: values.showContent,
    hideInGeneralLists: values.hideInGeneralLists,
    perContentPurchase: values.perContentPurchase,
    prices: values.prices.map(toPriceInput),
    publishAt,
  };
}

/** Значения формы -> input мутации `createProduct` (включает `type` — единственное
 * место, где тип продукта задаётся, он immutable после создания). */
export function buildProductInput(
  values: ProductFormValues,
  publishAt: string | null
): CreateProductInputType {
  return {
    type: values.type,
    ...buildCommonProductInput(values, publishAt),
  };
}

/** Значения формы -> input мутации `updateProduct` — БЕЗ `type`
 * (UpdateProductInput его не содержит; см. C1 в финальном ревью продуктов:
 * buildProductInput() раньше использовался и для update, что приводило к
 * отклонению всего запроса GraphQL-сервером на каждое сохранение формы
 * редактирования). */
export function buildUpdateProductInput(
  values: ProductFormValues,
  publishAt: string | null
): UpdateProductInputType {
  return buildCommonProductInput(values, publishAt);
}
